import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useToast } from '@/hooks/use-toast';
import { speakers } from '@/data/speakers';
import { Button } from "@/components/ui/button";
import PageHeader from '@/components/PageHeader';
import LogoScroller from '@/components/LogoScroller';

const SpeakerDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const speaker = speakers.find(s => s.slug === slug);

  if (!speaker) {
    return (
      <div className="min-h-screen bg-ai-dark">
        <Helmet>
          <title>Speaker Not Found - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
          <meta name="description" content="The requested speaker could not be found at AI Conference London." />
        </Helmet>
        <Header />
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl text-white text-center">Speaker not found</h1>
          </div>
        </div>
        <LogoScroller />
        <Footer />
      </div>
    );
  }

  // Generate structured data for the speaker
  const speakerStructuredData: any = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": speaker.name,
    "jobTitle": speaker.title,
    "image": speaker.image_url,
    "description": speaker.bio,
    "worksFor": {
      "@type": "Organization",
      "name": speaker.company_name || "Organization"
    },
    "event": {
      "@type": "Event",
      "name": "AI Conference London 2026",
      "startDate": "2026-11-25",
      "endDate": "2026-11-26",
      "location": {
        "@type": "Place",
        "name": "The Great Hall",
        "address": "Hornton St, Kensington, London W8 7NX"
      },
      "url": "https://aiconference.london/"
    }
  };

  // Add social profiles if available
  if (speaker.linkedin_url || speaker.twitter_url || speaker.github_url) {
    speakerStructuredData.sameAs = [];
    
    if (speaker.linkedin_url) {
      speakerStructuredData.sameAs.push(speaker.linkedin_url);
    }
    
    if (speaker.twitter_url) {
      speakerStructuredData.sameAs.push(speaker.twitter_url);
    }
    
    if (speaker.github_url) {
      speakerStructuredData.sameAs.push(speaker.github_url);
    }
  }

  // Check if this is WEKA (Liran's profile)
  const isWeka = speaker.company_name?.toLowerCase().includes('weka');
  
  // Check if this is Transforma Insights (Jim's profile)
  const isTransformaInsights = speaker.company_name?.toLowerCase().includes('transforma insights');
  
  // Check if this is AmpiFire (Jay's profile)
  const isAmpiFire = speaker.company_name?.toLowerCase().includes('ampifire');
  
  // Check if this is Anthropic (Pip's profile)
  const isAnthropic = speaker.company_name?.toLowerCase().includes('anthropic');

  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>{`${speaker.name}, ${speaker.company_name || ''} - AI World Congress 2026 - November 25-26, London UK - AI Conference`}</title>
        <meta name="description" content={`Learn from ${speaker.name}, ${speaker.title} at AI Conference London. Join us to hear their insights on AI technology and innovation.`} />
        <meta name="keywords" content={`${speaker.name}, ${speaker.company_name || ''}, ${speaker.title}, ${speaker.topic || 'AI speaker'}, AI conference London, artificial intelligence, tech speaker, AI World Congress 2026`} />
        <link rel="canonical" href={`https://aiconference.london/speakers/${slug}`} />
        <meta property="og:title" content={`${speaker.name}, ${speaker.company_name || ''} - AI World Congress 2026 - November 25-26, London UK - AI Conference`} />
        <meta property="og:description" content={`${speaker.name}, ${speaker.title} - Speaker at AI World Congress 2026`} />
        <meta property="og:url" content={`https://aiconference.london/speakers/${slug}`} />
        <meta property="og:image" content={speaker.image_url || "https://aiconference.london/lovable-uploads/og-banner-2026.jpg"} />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${speaker.name} - AI World Congress 2026 - November 25-26, London UK - AI Conference`} />
        <meta name="twitter:description" content={`${speaker.title} at ${speaker.company_name || 'AI World Congress 2026'}`} />
        <meta name="twitter:image" content={speaker.image_url || "https://aiconference.london/lovable-uploads/og-banner-2026.jpg"} />
        <script type="application/ld+json">
          {JSON.stringify(speakerStructuredData)}
        </script>
      </Helmet>
      <Header />
      <PageHeader
        title=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
      />
      <div className="relative z-10 -mt-16">
        <div className="relative py-16 md:py-24 overflow-hidden bg-gradient-to-b from-purple-900/80 to-black">
          <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
            <div className="absolute w-full h-[40%] bottom-0 bg-gradient-to-t from-black to-transparent"></div>
            <div className="absolute w-full h-[500px] bottom-0 left-0 right-0">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" xmlns="http://www.w3.org/2000/svg">
                <path 
                  fill="#000000" 
                  fillOpacity="1" 
                  d="M0,128L48,138.7C96,149,192,171,288,176C384,181,480,171,576,144C672,117,768,75,864,80C960,85,1056,139,1152,160C1248,181,1344,171,1392,165.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
                  className="animate-pulse"
                  style={{ animationDuration: '15s' }}
                ></path>
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-b from-white to-blue-50 border border-blue-200/30 shadow-lg rounded-lg p-8 mb-8">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-full md:w-1/3">
                    <img
                      src={speaker.image_url}
                      alt={speaker.name}
                      className="w-full rounded-lg shadow-lg"
                    />
                  </div>
                  <div className="w-full md:w-2/3">
                    <h1 className="text-3xl font-bold text-blue-900 mb-2">
                      {speaker.name}
                      {speaker.slug === 'kanishka-narayan-mp' && (
                        <span className="ml-2 text-destructive">(Past Speaker)</span>
                      )}
                    </h1>
                    <h2 className="text-xl text-blue-700 mb-4">{speaker.title}</h2>
                    {speaker.company_logo && (
                      <div className="mb-6 flex items-center justify-between">
                        <img
                          src={speaker.company_logo}
                          alt={`${speaker.company_name} logo`}
                          className={`${isWeka ? 'h-24' : isTransformaInsights ? 'h-48' : isAmpiFire ? 'h-20' : isAnthropic ? 'h-6' : 'h-12'}`}
                        />
                        <Link to="/tickets">
                          <Button 
                            className="ml-4 text-sm px-4 py-2 bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg"
                          >
                            Register Now
                          </Button>
                        </Link>
                      </div>
                    )}
                    {!speaker.company_logo && speaker.company_name && (
                      <div className="mb-6 flex items-center justify-between">
                        <span className="text-blue-700">{speaker.company_name}</span>
                        <Link to="/tickets">
                          <Button 
                            className="ml-4 text-sm px-4 py-2 bg-gradient-to-r from-[#F97316] to-[#ea384c] hover:opacity-90 text-white font-semibold shadow-lg"
                          >
                            Register Now
                          </Button>
                        </Link>
                      </div>
                    )}
                    <div className="prose prose-blue">
                      {speaker.slug === 'pip-white' && (
                        <p className="text-red-600 font-semibold mb-2">(Past Speaker)</p>
                      )}
                      <p className="text-gray-700 whitespace-pre-wrap">{speaker.bio}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default SpeakerDetail;
